const _excluded = ["testId"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, createElement as _createElement } from 'react';
import { format } from 'date-fns';
import Decimal from 'decimal.js';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConditionalWrapper, Spinner, Typography, WithInfoIcon } from 'seagull';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useLocaleKeys } from "../../../i18n";
import { SalesSummaryStatus } from "../../../models";
import { toDatestampString } from "../../../models/time";
import { selectors } from "../../../store/state-selectors";
import { utils } from "../../../utils";
import { getLastXMonths } from "../../../utils/date.utils";
import pluralize from "../../../utils/pluralize";
import { getXAxisTextForQuarterlyDate } from "../charts/charts.utils";
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import styles from "./SalesWidget.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const salesWidgetTextsPath = 'pages.dashboard.salesWidget';
const statusesOrder = [SalesSummaryStatus.Approved, SalesSummaryStatus.Reviewed, SalesSummaryStatus.Sent, SalesSummaryStatus.Expired, SalesSummaryStatus.Canceled, SalesSummaryStatus.Terminated];
const getStatusToText = texts => ({
  [SalesSummaryStatus.Approved]: texts.statusLabels.signed(),
  [SalesSummaryStatus.Reviewed]: texts.statusLabels.reviewed(),
  [SalesSummaryStatus.Sent]: texts.statusLabels.notReviewed(),
  [SalesSummaryStatus.Canceled]: texts.statusLabels.withdrawn(),
  [SalesSummaryStatus.Terminated]: texts.statusLabels.terminated(),
  [SalesSummaryStatus.Expired]: texts.statusLabels.expired()
});
const statusToColor = {
  [SalesSummaryStatus.Approved]: colors.blue700,
  [SalesSummaryStatus.Reviewed]: colors.purple700,
  [SalesSummaryStatus.Sent]: colors.purple300,
  [SalesSummaryStatus.Canceled]: colors.dark200,
  [SalesSummaryStatus.Terminated]: colors.red500,
  [SalesSummaryStatus.Expired]: colors.purple50
};
const renderBar = status => /*#__PURE__*/_createElement(Bar, _objectSpread(_objectSpread({}, commonBarProps), {}, {
  key: status,
  dataKey: item => item.statuses[status].total.toNumber(),
  stackId: "a",
  fill: statusToColor[status],
  shape: /*#__PURE__*/_jsx(CustomBarShape, {
    getHasRadius: item => {
      const statusIndex = statusesOrder.indexOf(status);
      const statusesAbove = statusesOrder.slice(statusIndex + 1);
      return !statusesAbove.some(s => item.statuses[s].total.gt(0));
    },
    getTestId: item => "".concat(status, "-for-").concat(toDatestampString(item.date))
  })
}));
export function RenderTooltip(_ref) {
  let {
      testId
    } = _ref,
    additionalProps = _objectWithoutProperties(_ref, _excluded);
  const {
    t
  } = useLocaleKeys(salesWidgetTextsPath);
  const statusToText = getStatusToText(t);
  return /*#__PURE__*/_jsx(CustomTooltip, _objectSpread(_objectSpread({}, additionalProps), {}, {
    skin: "dark",
    testId: "".concat(testId, "-tooltip"),
    getTitle: (label, payload) => {
      if (!payload || !payload.length) return '';
      const item = payload[0].payload;
      return format(item.date, 'MMMM');
    },
    getItems: payload => {
      if (!payload || !payload.length) return;
      const item = payload[0].payload;
      return statusesOrder.filter(s => item.statuses[s].total.gt(0)).map(s => ({
        id: s,
        label: "".concat(utils.formatNumberWithCommas(item.statuses[s].total), " ").concat(statusToText[s]),
        color: statusToColor[s]
      }));
    },
    getBody: payload => {
      if (!payload || !payload.length) return null;
      const item = payload[0].payload;
      const totalProposals = Object.values(item.statuses).reduce((acc, val) => acc.add(val.total), new Decimal(0)).toNumber();
      return "Out of ".concat(pluralize('proposal', totalProposals, true), " sent:");
    }
  }));
}
export function getLegendItems(data, texts) {
  const statusToText = getStatusToText(texts);
  return statusesOrder.filter(status => data.some(item => item.statuses[status].total.gt(0))).map(status => ({
    status,
    label: statusToText[status],
    color: statusToColor[status]
  }));
}
function SalesWidgetChart(_ref2) {
  let {
    testId,
    data,
    isTestMode
  } = _ref2;
  const {
    t
  } = useLocaleKeys(salesWidgetTextsPath);
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  const legendItems = useMemo(() => getLegendItems(data, t), [data, t]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": testId,
    className: styles.chart,
    children: /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: !isTestMode,
      wrapper: children => /*#__PURE__*/_jsx(ResponsiveContainer, {
        children: children
      }),
      children: /*#__PURE__*/_jsxs(BarChart, _objectSpread(_objectSpread({}, barChartProps), {}, {
        data: data,
        barCategoryGap: "10%",
        margin: {
          top: 20,
          left: 16,
          right: 16
        },
        children: [/*#__PURE__*/_jsx(CartesianGrid, _objectSpread({}, commonCartesianGridProps)), /*#__PURE__*/_jsx(Legend, {
          content: /*#__PURE__*/_jsx(CustomLegend, {
            testId: "".concat(testId, "-legend"),
            items: legendItems,
            gap: 16
          })
        }), /*#__PURE__*/_jsx(XAxis, _objectSpread(_objectSpread({}, commonXAxisProps), {}, {
          dataKey: item => item.date,
          height: 49,
          tick: /*#__PURE__*/_jsx(CustomXAxisTick, {
            tickHeight: 30,
            getText: getXAxisTextForQuarterlyDate
          })
        })), /*#__PURE__*/_jsx(YAxis, {
          axisLine: false,
          tickLine: false,
          width: 32,
          tickFormatter: value => value === 0 ? '' : utils.formatNumberWithCommas(value),
          fontSize: 12
        }), /*#__PURE__*/_jsx(Tooltip, {
          cursor: false,
          isAnimationActive: false,
          content: /*#__PURE__*/_jsx(RenderTooltip, {
            testId: testId
          })
        }), statusesOrder.map(renderBar)]
      }))
    })
  });
}
const TopCard = _ref3 => {
  let {
    label,
    info,
    value,
    loading,
    testId,
    linkTo
  } = _ref3;
  return /*#__PURE__*/_jsx(GraphWidgetTopCard, {
    testId: testId,
    linkTo: linkTo,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.topCard,
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(WithInfoIcon, {
          info: info,
          children: /*#__PURE__*/_jsx(Typography, {
            children: label
          })
        })
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        weight: "normal",
        testId: "".concat(testId, "-value"),
        children: loading ? /*#__PURE__*/_jsx(Spinner, {
          size: 30,
          testId: "".concat(testId, "-loading")
        }) : value
      })]
    })
  });
};
function getMockChartData(now) {
  return getLastXMonths(now, 12).map(date => ({
    date,
    statuses: {
      [SalesSummaryStatus.Sent]: {
        total: new Decimal(2)
      },
      [SalesSummaryStatus.Approved]: {
        total: new Decimal(3)
      },
      [SalesSummaryStatus.Terminated]: {
        total: new Decimal(0)
      },
      [SalesSummaryStatus.Canceled]: {
        total: new Decimal(1)
      },
      [SalesSummaryStatus.Reviewed]: {
        total: new Decimal(1)
      },
      [SalesSummaryStatus.Expired]: {
        total: new Decimal(0)
      }
    }
  }));
}
function MockSalesWidgetChart(_ref4) {
  let {
    isTestMode
  } = _ref4;
  const {
    getNow
  } = useIoContext();
  const data = useMemo(() => getMockChartData(getNow()), []);
  return /*#__PURE__*/_jsx(SalesWidgetChart, {
    data: data,
    testId: "sales-widget-mock-chart",
    isTestMode: isTestMode
  });
}
function SalesWidgetContent(_ref5) {
  let {
    loading,
    data,
    isTestMode
  } = _ref5;
  const hasSentAProposal = useStore(selectors.hasSentAProposal);
  const {
    t
  } = useLocaleKeys(salesWidgetTextsPath);
  const areCardsLoading = !data;
  return /*#__PURE__*/_jsxs(GraphWidgetLayout, {
    title: t.title(),
    loading: loading,
    bottomHeight: "l",
    testId: "sales-widget",
    children: [/*#__PURE__*/_jsx(TopCard, {
      loading: areCardsLoading,
      value: data ? data.formattedNumProposalsSentThisYear() : '',
      testId: "sales-widget-proposals-sent-this-year",
      label: t.proposalsSentThisYear.label(),
      info: t.proposalsSentThisYear.info(),
      linkTo: routes.proposalsReport
    }), /*#__PURE__*/_jsx(TopCard, {
      loading: areCardsLoading,
      value: data ? data.formattedMedianTimeToSign() : '',
      testId: "sales-widget-median-time-to-sign",
      label: t.medianTimeToSign.label(),
      info: t.medianTimeToSign.info()
    }), /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
      testId: "monthly-sales".concat(areCardsLoading ? '-loading' : ''),
      loading: areCardsLoading,
      loadingMsg: t.loadingMsg(),
      empty: data && data.isEmpty() && !hasSentAProposal,
      emptyMsg: t.emptyMsg(),
      loadingOrEmptyBg: () => /*#__PURE__*/_jsx(MockSalesWidgetChart, {
        isTestMode: isTestMode
      }),
      children: data && /*#__PURE__*/_jsx(SalesWidgetChart, {
        data: data.calendar(),
        testId: "sales-widget-chart",
        isTestMode: isTestMode
      })
    })]
  });
}
export default function SalesWidget(_ref6) {
  let {
    isTestMode
  } = _ref6;
  const initialData = useStore(state => state.salesSummary.data);
  const {
    fetchSalesSummary
  } = useStoreActions();
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    initialData: initialData,
    fetch: fetchSalesSummary,
    renderChild: _ref7 => {
      let {
        data,
        loading
      } = _ref7;
      return /*#__PURE__*/_jsx(SalesWidgetContent, {
        loading: loading,
        data: data,
        isTestMode: isTestMode
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(SalesWidgetContent, {
      loading: true,
      isTestMode: isTestMode
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testId: "widget-error"
    })
  });
}